/**
 * Overview Tab Component
 *
 * Data-forward account overview: real metrics from API, usage progress bars,
 * full account info, activity timeline, and internal notes.
 * Metrics: Documents, SMS, Emails, AI Credits.
 * Usage bars: Documents, Forms, SMS, Emails, AI Credits.
 */
import { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@respectly/ui';
import {
  FileText,
  MessageSquare,
  Mail,
  Sparkles,
  Calendar,
  Activity,
  Clock,
  Building2,
  UserCheck,
  Link2,
  Tag,
  Globe,
  Users,
  BookUser,
  Zap,
  Crown,
  Package,
  Star,
} from 'lucide-react';
import { cn } from '@respectly/ui';
import type { AccountDetail } from '@respectly/shared-types';
import { AccountStatusBadge, BillingStatusBadge } from '../AccountStatusBadge';
import { useAccountsStore } from '../../../stores/useAccountsStore';

// ============================================
// Types
// ============================================

interface OverviewTabProps {
  account: AccountDetail;
}

// ============================================
// Utility
// ============================================

function formatDate(d: Date | string): string {
  return new Date(d).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatRelativeTime(d: Date | string): string {
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return formatDate(d);
}

// ============================================
// Plan Hero
// ============================================

function getPlanTier(name: string): 'free' | 'standard' | 'pro' | 'enterprise' {
  const n = name.toLowerCase();
  if (n.includes('enterprise')) return 'enterprise';
  if (n.includes('pro')) return 'pro';
  if (n.includes('standard') || n.includes('basic') || n.includes('starter')) return 'standard';
  return 'free';
}

const PLAN_TIER_CONFIG = {
  free: {
    gradient: 'from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-800/50',
    border: 'border-slate-200 dark:border-slate-700',
    iconBg: 'bg-slate-200 dark:bg-slate-700',
    iconColor: 'text-slate-500 dark:text-slate-400',
    nameColor: 'text-slate-700 dark:text-slate-200',
    pill: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
    Icon: Package,
  },
  standard: {
    gradient: 'from-blue-50 to-sky-50/60 dark:from-blue-950/60 dark:to-sky-950/30',
    border: 'border-blue-200 dark:border-blue-800',
    iconBg: 'bg-blue-100 dark:bg-blue-900',
    iconColor: 'text-blue-500 dark:text-blue-400',
    nameColor: 'text-blue-700 dark:text-blue-300',
    pill: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300',
    Icon: Zap,
  },
  pro: {
    gradient: 'from-violet-50 to-purple-50/60 dark:from-violet-950/60 dark:to-purple-950/30',
    border: 'border-violet-200 dark:border-violet-800',
    iconBg: 'bg-violet-100 dark:bg-violet-900',
    iconColor: 'text-violet-500 dark:text-violet-400',
    nameColor: 'text-violet-700 dark:text-violet-300',
    pill: 'bg-violet-100 text-violet-600 dark:bg-violet-900 dark:text-violet-300',
    Icon: Star,
  },
  enterprise: {
    gradient: 'from-amber-50 to-orange-50/60 dark:from-amber-950/60 dark:to-orange-950/30',
    border: 'border-amber-200 dark:border-amber-800',
    iconBg: 'bg-amber-100 dark:bg-amber-900',
    iconColor: 'text-amber-500 dark:text-amber-400',
    nameColor: 'text-amber-700 dark:text-amber-300',
    pill: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
    Icon: Crown,
  },
} as const;

function PlanHero({ planName, status }: { planName: string; status: string }) {
  const tier = getPlanTier(planName);
  const cfg = PLAN_TIER_CONFIG[tier];
  const { Icon } = cfg;
  const isActive = status === 'active' || status === 'trial';

  return (
    <div
      className={cn(
        'mb-3 rounded-lg border bg-gradient-to-br px-4 py-3.5',
        cfg.gradient,
        cfg.border
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={cn('rounded-md p-2 shrink-0', cfg.iconBg)}>
            <Icon className={cn('h-4 w-4', cfg.iconColor)} />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-medium uppercase tracking-widest text-slate-400 dark:text-slate-500 leading-none mb-1">
              Current Plan
            </p>
            <p className={cn('text-base font-bold leading-none truncate', cfg.nameColor)}>
              {planName}
            </p>
          </div>
        </div>
        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
            isActive ? cfg.pill : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
          )}
        >
          {isActive ? 'Active' : status}
        </span>
      </div>
    </div>
  );
}

// ============================================
// Metric Card
// ============================================

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  accentColor: string;
  bgColor: string;
  iconColor: string;
  isLoading?: boolean;
}

function MetricCard({
  title,
  value,
  icon: Icon,
  accentColor,
  bgColor,
  iconColor,
  isLoading,
}: MetricCardProps) {
  return (
    <Card className={cn('border-l-2 overflow-hidden', accentColor)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
              {title}
            </p>
            {isLoading ? (
              <Skeleton className="h-8 w-20 mt-1" />
            ) : (
              <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
                {typeof value === 'number' ? value.toLocaleString() : value}
              </p>
            )}
          </div>
          <div className={cn('rounded-lg p-2 shrink-0', bgColor)}>
            <Icon className={cn('h-4 w-4', iconColor)} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// Usage Bar
// ============================================

interface UsageBarProps {
  label: string;
  used: number;
  limit: number;
  icon: React.ComponentType<{ className?: string }>;
}

function UsageBar({ label, used, limit, icon: Icon }: UsageBarProps) {
  const unlimited = limit === 0;
  const pct = unlimited ? 0 : Math.min((used / limit) * 100, 100);
  const isCritical = !unlimited && pct >= 90;
  const isWarning = !unlimited && pct >= 70 && !isCritical;
  const barColor = isCritical ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-emerald-500';
  const textColor = isCritical
    ? 'text-red-600 dark:text-red-400'
    : isWarning
    ? 'text-amber-600 dark:text-amber-400'
    : 'text-slate-500 dark:text-slate-400';

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400">
          <Icon className="h-3.5 w-3.5 shrink-0" />
          <span>{label}</span>
        </div>
        <span className={cn('text-xs font-mono font-medium', textColor)}>
          {unlimited ? `${used.toLocaleString()} / ∞` : `${used.toLocaleString()} / ${limit.toLocaleString()}`}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
        {unlimited ? (
          <div className="h-full w-0 rounded-full bg-slate-300 dark:bg-slate-600" />
        ) : (
          <div
            className={cn('h-full rounded-full transition-all duration-700 ease-out', barColor)}
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
    </div>
  );
}

// ============================================
// Info Row
// ============================================

interface InfoRowProps {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  capitalize?: boolean;
  highlight?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
}

function InfoRow({ label, value, mono, capitalize, highlight, icon: Icon }: InfoRowProps) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 shrink-0">
        {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
        <span>{label}</span>
      </div>
      <span
        className={cn(
          'text-sm text-right max-w-[58%] truncate',
          typeof value === 'string'
            ? [
                'text-slate-900 dark:text-white',
                mono && 'font-mono text-xs',
                capitalize && 'capitalize',
                highlight && 'font-semibold',
              ]
            : ''
        )}
      >
        {value}
      </span>
    </div>
  );
}

// ============================================
// Event Config
// ============================================

const EVENT_LABELS: Record<string, string> = {
  created: 'Account Created',
  activated: 'Account Activated',
  suspended: 'Account Suspended',
  reactivated: 'Account Reactivated',
  cancelled: 'Account Cancelled',
  plan_changed: 'Plan Changed',
  billing_status_changed: 'Billing Status Changed',
};

const EVENT_DOT_COLORS: Record<string, string> = {
  created: 'bg-emerald-500',
  activated: 'bg-blue-500',
  suspended: 'bg-amber-500',
  reactivated: 'bg-emerald-500',
  cancelled: 'bg-red-500',
  plan_changed: 'bg-violet-500',
  billing_status_changed: 'bg-orange-500',
};

// ============================================
// Main Component
// ============================================

export function OverviewTab({ account }: OverviewTabProps) {
  const { accountUsage, isLoadingUsage, loadAccountUsage } = useAccountsStore();

  useEffect(() => {
    loadAccountUsage(account.id);
  }, [account.id, loadAccountUsage]);

  const usageLoading = isLoadingUsage && !accountUsage;

  // Derive owner email from users array if not on account directly
  const ownerEmail =
    account.ownerEmail ??
    account.users?.find((u) => u.role === 'admin')?.email ??
    account.users?.[0]?.email ??
    null;

  return (
    <div className="space-y-5">

      {/* ── Metric Cards (this month) ────────────────── */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Documents"
          value={accountUsage?.documentsCreated ?? 0}
          icon={FileText}
          accentColor="border-l-blue-500"
          bgColor="bg-blue-50 dark:bg-blue-900/20"
          iconColor="text-blue-500"
          isLoading={usageLoading}
        />
        <MetricCard
          title="SMS Sent"
          value={accountUsage?.smsSent ?? 0}
          icon={MessageSquare}
          accentColor="border-l-violet-500"
          bgColor="bg-violet-50 dark:bg-violet-900/20"
          iconColor="text-violet-500"
          isLoading={usageLoading}
        />
        <MetricCard
          title="Emails Sent"
          value={accountUsage?.emailsSent ?? 0}
          icon={Mail}
          accentColor="border-l-emerald-500"
          bgColor="bg-emerald-50 dark:bg-emerald-900/20"
          iconColor="text-emerald-500"
          isLoading={usageLoading}
        />
        <MetricCard
          title="AI Credits"
          value={accountUsage?.aiCreditsUsed ?? 0}
          icon={Sparkles}
          accentColor="border-l-amber-500"
          bgColor="bg-amber-50 dark:bg-amber-900/20"
          iconColor="text-amber-500"
          isLoading={usageLoading}
        />
      </div>

      {/* ── Account Info + Plan & Billing ─────────────── */}
      <div className="grid gap-5 lg:grid-cols-2">

        {/* Account Information */}
        <Card>
          <CardHeader className="pb-0 pt-4 px-4">
            <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Account Information
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pt-1 pb-3">
            <InfoRow label="Account Name" value={account.name} icon={Building2} />
            <InfoRow label="Slug" value={`/${account.slug}`} mono icon={Link2} />
            {account.industry && <InfoRow label="Industry" value={account.industry} icon={Tag} />}
            {account.source && <InfoRow label="Source" value={account.source} capitalize icon={Globe} />}
            {ownerEmail && <InfoRow label="Owner Email" value={ownerEmail} mono icon={Mail} />}
            <InfoRow label="Users" value={String(account.userCount)} icon={Users} />
            <InfoRow label="Contacts" value={account.contactCount.toLocaleString()} icon={BookUser} />
            <InfoRow
              label="Status"
              value={<AccountStatusBadge status={account.status} size="sm" />}
              icon={Activity}
            />
            <InfoRow
              label="Billing Status"
              value={<BillingStatusBadge status={account.billingStatus} size="sm" />}
              icon={Activity}
            />
            <InfoRow label="Created" value={formatDate(account.createdAt)} icon={Calendar} />
            {account.lastActivityAt && (
              <InfoRow
                label="Last Activity"
                value={formatRelativeTime(account.lastActivityAt)}
                icon={Clock}
              />
            )}
            {account.assignedTo && (
              <InfoRow
                label="Assigned To"
                value={`${account.assignedTo.firstName} ${account.assignedTo.lastName}`}
                icon={UserCheck}
              />
            )}
          </CardContent>
        </Card>

        {/* Plan & Billing */}
        <Card>
          <CardHeader className="pb-0 pt-4 px-4">
            <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Plan & Billing
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pt-2 pb-3">
            <PlanHero planName={account.plan.name} status={account.status} />
            <InfoRow
              label="Helcim Customer ID"
              value={account.helcimCustomerId ?? '—'}
              mono
            />
            {account.status === 'trial' && account.trialEndsAt && (
              <div className="mt-3 rounded-md bg-blue-50 px-3 py-2 dark:bg-blue-900/30">
                <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-400">
                  <Calendar className="h-4 w-4 shrink-0" />
                  <span>Trial ends {formatDate(account.trialEndsAt)}</span>
                </div>
              </div>
            )}
            {account.status === 'suspended' && account.suspendedAt && (
              <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 dark:bg-amber-900/30">
                <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
                  <Activity className="h-4 w-4 shrink-0" />
                  <span>Suspended on {formatDate(account.suspendedAt)}</span>
                </div>
              </div>
            )}
            {account.status === 'cancelled' && account.cancelledAt && (
              <div className="mt-3 rounded-md bg-red-50 px-3 py-2 dark:bg-red-900/30">
                <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                  <Activity className="h-4 w-4 shrink-0" />
                  <span>Cancelled on {formatDate(account.cancelledAt)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Usage This Month ──────────────────────────── */}
      {usageLoading ? (
        <Card>
          <CardContent className="p-4 space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="space-y-2">
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <Skeleton className="h-1.5 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      ) : accountUsage ? (
        <Card>
          <CardHeader className="pb-2 pt-4 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                Usage This Month
              </CardTitle>
              <span className="text-xs font-mono text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                {accountUsage.period}
              </span>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-4">
            <UsageBar
              label="Documents"
              used={accountUsage.documentsCreated}
              limit={accountUsage.documentsLimit}
              icon={FileText}
            />
            <UsageBar
              label="Form Submissions"
              used={accountUsage.formsSubmitted}
              limit={accountUsage.formsLimit}
              icon={WpformsIcon}
            />
            <UsageBar
              label="SMS Messages"
              used={accountUsage.smsSent}
              limit={accountUsage.smsLimit}
              icon={MessageSquare}
            />
            <UsageBar
              label="Emails"
              used={accountUsage.emailsSent}
              limit={accountUsage.emailsLimit}
              icon={Mail}
            />
            <UsageBar
              label="AI Credits"
              used={accountUsage.aiCreditsUsed}
              limit={accountUsage.aiCreditsLimit}
              icon={Sparkles}
            />
          </CardContent>
        </Card>
      ) : null}

      {/* ── Recent Activity Timeline ──────────────────── */}
      {account.recentActivity && account.recentActivity.length > 0 && (
        <Card>
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="relative space-y-4 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-px before:bg-slate-200 dark:before:bg-slate-700">
              {account.recentActivity.slice(0, 5).map((event) => (
                <div key={event.id} className="relative flex items-start gap-3 pl-5">
                  <div
                    className={cn(
                      'absolute left-0 top-1.5 h-3.5 w-3.5 rounded-full border-2 border-white dark:border-slate-900',
                      EVENT_DOT_COLORS[event.event] ?? 'bg-slate-400'
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-white leading-snug">
                      {EVENT_LABELS[event.event] || event.event}
                    </p>
                    {event.reason && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">
                        {event.reason}
                      </p>
                    )}
                    {event.performedBy && (
                      <p className="text-xs text-slate-400 mt-0.5">
                        by {event.performedBy.firstName} {event.performedBy.lastName}
                      </p>
                    )}
                  </div>
                  <span className="text-xs text-slate-400 shrink-0 mt-0.5">
                    {formatRelativeTime(event.createdAt)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Internal Notes ────────────────────────────── */}
      {account.notes && (
        <Card className="border-amber-100 dark:border-amber-900/40 bg-amber-50/50 dark:bg-amber-900/10">
          <CardHeader className="pb-0 pt-3 px-4">
            <CardTitle className="text-sm font-semibold text-amber-800 dark:text-amber-400">
              Internal Notes
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3 pt-1">
            <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
              {account.notes}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Inline SVG icon for forms (lucide doesn't have WpForms)
function WpformsIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="7" y1="8" x2="17" y2="8" />
      <line x1="7" y1="12" x2="17" y2="12" />
      <line x1="7" y1="16" x2="13" y2="16" />
    </svg>
  );
}
